import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  QrCode, Search, CheckCircle, XCircle, Users, Clock,
  Camera, RefreshCw, Loader2, UserPlus, Lock, Calendar, MapPin
} from 'lucide-react';
import './EventCheckIn.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EventCheckIn = () => {
  const { eventSlug } = useParams();
  const [eventInfo, setEventInfo] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_registered: 0, checked_in: 0, pending: 0 });
  
  // Scanning state
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Walk-in state
  const [showWalkIn, setShowWalkIn] = useState(false);
  const [walkInData, setWalkInData] = useState({ name: '', email: '', phone: '' });
  const [walkInLoading, setWalkInLoading] = useState(false);

  useEffect(() => {
    fetchEventInfo();
  }, [eventSlug]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
      const interval = setInterval(fetchStats, 30000); // Refresh every 30s
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, eventSlug, pin]);

  const fetchEventInfo = async () => {
    try {
      const res = await fetch(`${API_URL}/api/events/${eventSlug}/check-in/info`);
      if (res.ok) {
        const data = await res.json();
        setEventInfo(data);
        // If no PIN required, auto-authenticate
        if (!data.has_pin) {
          setIsAuthenticated(true);
        }
      } else {
        toast.error('Event not found');
      }
    } catch (error) {
      toast.error('Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/events/${eventSlug}/check-in/stats?pin=${pin}`);
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setPinError('');
    
    try {
      const res = await fetch(`${API_URL}/api/events/${eventSlug}/check-in/verify-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin })
      });
      
      if (res.ok) {
        setIsAuthenticated(true);
        toast.success('Access granted!');
      } else {
        setPinError('Invalid PIN');
      }
    } catch (error) {
      setPinError('Failed to verify PIN');
    }
  };

  const startScanning = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setScanning(true);
        scanQRCode();
      }
    } catch (error) {
      toast.error('Unable to access camera. Please use manual search.');
    }
  };

  const stopScanning = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    setScanning(false);
  };

  const scanQRCode = () => {
    if (!scanning) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    
    // Use browser's BarcodeDetector if available
    if ('BarcodeDetector' in window) {
      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      detector.detect(canvas).then(codes => {
        if (codes.length > 0) {
          handleQRScanned(codes[0].rawValue);
        } else {
          requestAnimationFrame(scanQRCode);
        }
      }).catch(() => {
        requestAnimationFrame(scanQRCode);
      });
    } else {
      // Fallback: manual entry
      toast.info('QR scanning not supported. Please use manual search.');
      stopScanning();
    }
  };

  const handleQRScanned = async (qrData) => {
    stopScanning();
    setScanResult({ loading: true });
    
    try {
      const res = await fetch(`${API_URL}/api/events/${eventSlug}/check-in/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qr_data: qrData, pin })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setScanResult(data);
        fetchStats();
        if (data.status === 'checked_in') {
          toast.success(`${data.name} checked in!`);
        } else if (data.status === 'already_checked_in') {
          toast.info(`${data.name} was already checked in`);
        }
      } else {
        setScanResult({ error: data.detail });
        toast.error(data.detail);
      }
    } catch (error) {
      setScanResult({ error: 'Scan failed' });
      toast.error('Failed to process QR code');
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const res = await fetch(
        `${API_URL}/api/events/${eventSlug}/check-in/search?q=${encodeURIComponent(searchQuery)}&pin=${pin}`
      );
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data);
      }
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleManualCheckIn = async (registrationId, name) => {
    try {
      const res = await fetch(`${API_URL}/api/events/${eventSlug}/check-in/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ registration_id: registrationId, pin })
      });
      
      if (res.ok) {
        toast.success(`${name} checked in!`);
        // Update search results
        setSearchResults(prev => prev.map(r => 
          r.id === registrationId ? { ...r, checked_in: true } : r
        ));
        fetchStats();
      } else {
        const data = await res.json();
        toast.error(data.detail);
      }
    } catch (error) {
      toast.error('Check-in failed');
    }
  };

  const handleWalkIn = async (e) => {
    e.preventDefault();
    setWalkInLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/events/${eventSlug}/walk-in`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...walkInData, pin })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`${walkInData.name} registered and checked in!`);
        setWalkInData({ name: '', email: '', phone: '' });
        setShowWalkIn(false);
        fetchStats();
      } else {
        toast.error(data.detail);
      }
    } catch (error) {
      toast.error('Walk-in registration failed');
    } finally {
      setWalkInLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="check-in-page loading-state">
        <Loader2 size={40} className="spinner" />
        <p>Loading event...</p>
      </div>
    );
  }

  if (!eventInfo) {
    return (
      <div className="check-in-page error-state">
        <XCircle size={48} />
        <h2>Event Not Found</h2>
        <p>This event doesn't exist or check-in is not available.</p>
      </div>
    );
  }

  // PIN Entry Screen
  if (!isAuthenticated && eventInfo.has_pin) {
    return (
      <div className="check-in-page pin-screen">
        <div className="pin-card">
          <Lock size={48} className="pin-icon" />
          <h1>Event Check-In</h1>
          <h2>{eventInfo.title}</h2>
          <p className="event-details">
            <Calendar size={16} /> {eventInfo.date}
            <span className="separator">•</span>
            <MapPin size={16} /> {eventInfo.venue}
          </p>
          
          <form onSubmit={handlePinSubmit} className="pin-form">
            <label>Enter Check-In PIN</label>
            <input
              type="password"
              maxLength={6}
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Enter PIN"
              autoFocus
              data-testid="pin-input"
            />
            {pinError && <span className="pin-error">{pinError}</span>}
            <button type="submit" className="btn btn-primary" data-testid="pin-submit">
              Access Check-In
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Main Check-In Interface
  return (
    <div className="check-in-page" data-testid="check-in-page">
      <header className="check-in-header">
        <div className="event-info">
          <h1>{eventInfo.title}</h1>
          <p><Calendar size={16} /> {eventInfo.date} • <MapPin size={16} /> {eventInfo.venue}</p>
        </div>
      </header>

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stat">
          <span className="stat-value">{stats.checked_in}</span>
          <span className="stat-label">Checked In</span>
        </div>
        <div className="stat">
          <span className="stat-value">{stats.pending}</span>
          <span className="stat-label">Pending</span>
        </div>
        <div className="stat">
          <span className="stat-value">{stats.total_registered}</span>
          <span className="stat-label">Total</span>
        </div>
      </div>

      <div className="check-in-content">
        {/* QR Scanner Section */}
        <section className="scanner-section">
          <h2><QrCode size={24} /> Scan QR Code</h2>
          
          {scanning ? (
            <div className="scanner-active">
              <video ref={videoRef} autoPlay playsInline />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              <button className="btn btn-secondary" onClick={stopScanning}>
                Stop Scanning
              </button>
            </div>
          ) : (
            <button className="btn btn-primary scan-btn" onClick={startScanning} data-testid="start-scan">
              <Camera size={20} />
              Start Scanning
            </button>
          )}
          
          {scanResult && !scanResult.loading && (
            <div className={`scan-result ${scanResult.error ? 'error' : 'success'}`}>
              {scanResult.error ? (
                <>
                  <XCircle size={32} />
                  <p>{scanResult.error}</p>
                </>
              ) : (
                <>
                  <CheckCircle size={32} />
                  <h3>{scanResult.name}</h3>
                  <p>{scanResult.email}</p>
                  <span className="status-badge">
                    {scanResult.status === 'already_checked_in' ? 'Already Checked In' : 'Checked In'}
                  </span>
                </>
              )}
              <button className="btn btn-secondary" onClick={() => setScanResult(null)}>
                <RefreshCw size={16} /> Scan Another
              </button>
            </div>
          )}
        </section>

        {/* Manual Search Section */}
        <section className="search-section">
          <h2><Search size={24} /> Manual Search</h2>
          
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              data-testid="search-input"
            />
            <button 
              className="btn btn-primary" 
              onClick={handleSearch}
              disabled={searching}
            >
              {searching ? <Loader2 size={18} className="spinner" /> : <Search size={18} />}
            </button>
          </div>
          
          {searchResults.length > 0 && (
            <div className="search-results">
              {searchResults.map(attendee => (
                <div key={attendee.id} className={`attendee-card ${attendee.checked_in ? 'checked-in' : ''}`}>
                  <div className="attendee-info">
                    <span className="attendee-name">{attendee.name}</span>
                    <span className="attendee-email">{attendee.email}</span>
                    {attendee.phone && <span className="attendee-phone">{attendee.phone}</span>}
                  </div>
                  <div className="attendee-action">
                    {attendee.checked_in ? (
                      <span className="checked-badge">
                        <CheckCircle size={16} /> Checked In
                      </span>
                    ) : (
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={() => handleManualCheckIn(attendee.id, attendee.name)}
                        data-testid={`checkin-${attendee.id}`}
                      >
                        Check In
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Walk-In Registration Section */}
        <section className="walkin-section">
          <h2><UserPlus size={24} /> Walk-In Registration</h2>
          
          {!showWalkIn ? (
            <button className="btn btn-secondary" onClick={() => setShowWalkIn(true)} data-testid="show-walkin">
              <UserPlus size={18} /> Register Walk-In Guest
            </button>
          ) : (
            <form onSubmit={handleWalkIn} className="walkin-form">
              <div className="form-group">
                <input
                  type="text"
                  placeholder="Full Name *"
                  value={walkInData.name}
                  onChange={(e) => setWalkInData({...walkInData, name: e.target.value})}
                  required
                  data-testid="walkin-name"
                />
              </div>
              <div className="form-group">
                <input
                  type="email"
                  placeholder="Email Address *"
                  value={walkInData.email}
                  onChange={(e) => setWalkInData({...walkInData, email: e.target.value})}
                  required
                  data-testid="walkin-email"
                />
              </div>
              <div className="form-group">
                <input
                  type="tel"
                  placeholder="Phone (optional)"
                  value={walkInData.phone}
                  onChange={(e) => setWalkInData({...walkInData, phone: e.target.value})}
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowWalkIn(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={walkInLoading}>
                  {walkInLoading ? <Loader2 size={18} className="spinner" /> : 'Register & Check In'}
                </button>
              </div>
            </form>
          )}
        </section>
      </div>
    </div>
  );
};

export default EventCheckIn;
